import type { LinearRGB } from '../../../core/color';
import type { Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { horizontalCap, orientedFace, planPoint, triangulateRing } from '../solids';
import { buildConventTrees } from './conventTrees';
import { corners, type PlanRect } from './genericBlock';
import readings from './minderbroedersPlan.json';

export function buildFriaryGroundRect(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  r: PlanRect,
  height: (p: Vec2) => number
): void {
  const nx = Math.ceil((r.u1 - r.u0) / 2),
    ny = Math.ceil((r.v1 - r.v0) / 2);
  let lowest = Infinity;
  for (let row = 0; row < ny; row++)
    for (let col = 0; col < nx; col++) {
      const rect = {
        u0: r.u0 + ((r.u1 - r.u0) * col) / nx,
        u1: r.u0 + ((r.u1 - r.u0) * (col + 1)) / nx,
        v0: r.v0 + ((r.v1 - r.v0) * row) / ny,
        v1: r.v0 + ((r.v1 - r.v0) * (row + 1)) / ny,
      };
      for (const tri of triangulateRing(corners(rect))) {
        for (const p of tri) lowest = Math.min(lowest, height(p));
        orientedFace(
          builder,
          style,
          frame,
          tri.map((p) => planPoint(p[0], height(p), p[1])),
          [0, 1, 0]
        );
      }
    }
  // A shallow soil volume closes every sloping patch below the local ground.
  const bottom = lowest - 0.05,
    ring = corners(r);
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % 4]!,
      n = i % 2 === 0 ? nx : ny;
    const p = (t: number): Vec2 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    for (let k = 0; k < n; k++) {
      const from = p(k / n),
        to = p((k + 1) / n);
      orientedFace(
        builder,
        style,
        frame,
        [
          planPoint(from[0], bottom, from[1]),
          planPoint(to[0], bottom, to[1]),
          planPoint(to[0], height(to), to[1]),
          planPoint(from[0], height(from), from[1]),
        ],
        [to[1] - from[1], 0, to[0] - from[0]]
      );
    }
  });
  horizontalCap(builder, style, frame, ring, bottom, false);
}

export function minderbroedersTreePoints(): Vec2[] {
  const r = readings.orchard,
    points: Vec2[] = [];
  for (let row = 0; row < r.rows; row++)
    for (let col = 0; col < r.columns; col++)
      points.push([
        r.u0 + ((col + 0.5) * (r.u1 - r.u0)) / r.columns,
        r.v0 + ((row + 0.5) * (r.v1 - r.v0)) / r.rows,
      ]);
  return points;
}

export function buildFriaryOrchard(
  builder: MeshBuilder,
  ground: VertexStyle,
  garden: VertexStyle,
  frame: Frame,
  colors: { timber: LinearRGB; foliage: LinearRGB },
  terrain: (p: Vec2) => number
): void {
  buildConventTrees(
    builder,
    ground,
    garden,
    frame,
    colors,
    terrain,
    minderbroedersTreePoints(),
    'friary'
  );
}
